import { useCallback } from 'react';
import { isTypingIn } from './keys';
import useShortcut from './useShortcut';

/**
 * ctrl+F and `/` focus "the search box" - which one depends on the page.
 *
 * A page has up to two: its own search (YouTube, Spotify, SoundCloud,
 * Mixcloud) and the filter over a list of tracks. The YouTube page has
 * both at once, and their ids collide, so a lookup by id would not do.
 * Instead every box joins this list while it is mounted, through the ref
 * `useSearchBox` returns, and the key goes to the page's search box when
 * there is one and to the newest filter otherwise. iTunes' ⌘F went to the
 * search field, and a page's search is the closer thing to it.
 */

export type SearchBoxKind = 'search' | 'filter';

interface Box {
  kind: SearchBoxKind;
  el: HTMLInputElement;
}

const boxes: Box[] = [];

/** Focuses the box the key should go to. False when there is none. */
export function focusSearchBox() {
  const live = boxes.filter((x) => x.el.isConnected);
  const box =
    [...live].reverse().find((x) => x.kind === 'search') ||
    [...live].reverse().find((x) => x.kind === 'filter');

  if (!box) {
    return false;
  }

  box.el.focus();
  box.el.select();

  return true;
}

/** Test seam. */
export function clearSearchBoxes() {
  boxes.length = 0;
}

/**
 * Makes an input one of the search boxes for as long as it is mounted.
 * Attach the returned ref to it. Also declares the shortcut, so the overlay
 * lists it whenever a box is on the page and never when none is.
 */
export default function useSearchBox(kind: SearchBoxKind) {
  useShortcut({
    id: 'focus-search',
    keys: [{ key: 'f', mod: true }, { key: '/' }],
    group: 'Library',
    description: 'Find: focus the search or filter box',
    // ctrl+F from inside a box goes to the box; a slash typed in one is a slash.
    whileTyping: true,
    run: (ev) => {
      if (ev.key === '/' && isTypingIn(ev)) {
        return false;
      }

      return focusSearchBox();
    },
  });

  return useCallback(
    function ref(el: HTMLInputElement | null) {
      const index = boxes.findIndex((x) => x.kind === kind && !x.el.isConnected);

      if (index !== -1) {
        boxes.splice(index, 1);
      }

      if (el) {
        boxes.push({ kind, el });
      }
    },
    [kind],
  );
}
